/**
 * First-run setup — the ONE wizard component.
 *
 * It sequences existing surfaces rather than inventing new ones: the row is the
 * `GroupCard` + `Tag` + `Checkbox` + `BrandMark` vocabulary the settings panels
 * already use, the agent step reuses `AgentRow` and the probed readiness behind
 * `useAgentEngine`, module installs go through the existing extensions RPCs, and
 * the search step mounts `SearchIndexingPanel`, which already reports each
 * model's size and whether it is cached.
 *
 * Deliberately one file: the screens are a linear sequence over one piece of
 * state, and splitting them into a component each would spread that state
 * across files for no gain. The step arithmetic and resume rules are pure and
 * live in `setupState.ts`, where they are tested without a DOM.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import type { JSX } from "react";

import { BrandMark, Checkbox, Tag } from "../../components/ui";
import { useRpcMutation, useRpcQuery } from "../../runtime/hooks/useRpc";
import { SourceConnect } from "../../components/integrations/SourceConnect";
import { SearchIndexingPanel } from "../settings/SearchIndexingPanel";
import { AgentRow, engineMeta } from "../settings/aiModelsShared";
import { readinessLabel } from "../settings/helpers";
import { useAgentEngine } from "../settings/hooks/useAgentEngine";
import {
  CAPABILITIES,
  defaultPicks,
  nextStep,
  prevStep,
  resolve,
  stepNumber,
  type Capability,
  type Step,
} from "./setupState";

/** Mirrors `services::settings::setup_state::SetupState`. */
export interface SetupState {
  readonly version: number;
  readonly completed: boolean;
  readonly current_step: string;
  readonly capabilities: readonly string[];
  readonly deferred: readonly string[];
  readonly engine: string | null;
  readonly indexer_model: string | null;
}

interface SourceInfo {
  readonly source_id: string;
  readonly auth_type?: string | null;
  readonly connectable?: boolean;
  readonly unavailable_reason?: string | null;
}

function brandFor(id: string): "google" | "telegram" | "x" | "linkedin" | null {
  if (id === "google" || id === "telegram" || id === "x" || id === "linkedin") return id;
  if (id === "anysite") return "linkedin";
  if (id === "email" || id === "meetings") return "google";
  return null;
}

/**
 * Human name for a source id. `anysite` is the connector behind LinkedIn —
 * the person picked "LinkedIn" and has no reason to learn the connector's
 * name; where it genuinely cannot run, `unavailable_reason` says so.
 */
function sourceTitle(id: string): string {
  if (id === "anysite") return "LinkedIn";
  if (id === "x") return "X";
  return id.charAt(0).toUpperCase() + id.slice(1);
}

/**
 * One list row.
 *
 * With no `onClick` it renders as a plain container, NOT a disabled button:
 * a row that lights up on hover and shows a pointer, then does nothing when
 * clicked, is the single most common way this screen was read as broken.
 */
function Row({
  id,
  title,
  blurb,
  trailing,
  onClick,
}: {
  readonly id: string;
  readonly title: string;
  readonly blurb?: string;
  readonly trailing: JSX.Element;
  readonly onClick?: () => void;
}): JSX.Element {
  const brand = brandFor(id);
  const inner = (
    <>
      {brand !== null ? <BrandMark brand={brand} size={22} /> : <span className="w-[22px]" />}
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-medium text-content">{title}</span>
        {blurb !== undefined && (
          <span className="block text-xs text-content-tertiary">{blurb}</span>
        )}
      </span>
      {trailing}
    </>
  );
  const shared = "w-full flex items-center gap-2.5 px-4 py-3 text-left";
  if (onClick === undefined) {
    return <div className={shared}>{inner}</div>;
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${shared} hover:bg-surface-hover transition-colors cursor-pointer`}
    >
      {inner}
    </button>
  );
}

function Card({ children }: { readonly children: React.ReactNode }): JSX.Element {
  return (
    <div className="rounded-xl bg-surface-tertiary border border-edge overflow-hidden divide-y divide-edge">
      {children}
    </div>
  );
}

export function SetupFlow({
  initialStep,
  initialPicks,
  initialEngine,
  initialFailed,
  onFinished,
}: {
  /** Where a previous, interrupted run stopped, and what it recorded. */
  readonly initialStep?: Step;
  readonly initialPicks?: readonly string[];
  readonly initialEngine?: string | null;
  readonly initialFailed?: readonly string[];
  readonly onFinished: () => void;
}): JSX.Element {
  const [step, setStep] = useState<Step>(initialStep ?? "welcome");
  const [picks, setPicks] = useState<readonly string[]>(initialPicks ?? defaultPicks());
  const [installed, setInstalled] = useState<readonly string[]>([]);
  const [failed, setFailed] = useState<readonly string[]>(initialFailed ?? []);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [connecting, setConnecting] = useState<{ id: string; authType: string } | null>(null);
  const [connected, setConnected] = useState<readonly string[]>([]);
  const [engine, setEngine] = useState<string | null>(initialEngine ?? null);

  // The backend already knows which sources can be connected and why not:
  // `source.list` returns auth_type, connectable and unavailable_reason.
  const sourceList = useRpcQuery<{ sources?: readonly SourceInfo[] } | readonly SourceInfo[]>(
    ["source", "list"],
    "source.list",
  );
  const saveSetup = useRpcMutation<SetupState>("setup.update");
  const refreshCatalog = useRpcMutation("extensions.catalog.refresh");
  const install = useRpcMutation<never, { key: string }>("extensions.install");
  // Probed readiness, not the static engine list — see
  // `services::agents::readiness`.
  const agents = useAgentEngine();

  const plan = useMemo(() => resolve(picks), [picks]);
  const hasSources = plan.sources.length > 0;

  const toggle = useCallback((id: string) => {
    setPicks((prev) => (prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]));
  }, []);

  const persist = useCallback(
    async (patch: Partial<SetupState>) => {
      await saveSetup.mutateAsync({
        version: 1,
        completed: false,
        current_step: step,
        capabilities: picks,
        deferred: failed,
        engine,
        indexer_model: null,
        ...patch,
      } as unknown as Record<string, unknown>);
    },
    [saveSetup, step, picks, failed, engine],
  );

  /**
   * Move to another screen and record it.
   *
   * Every transition is written, so quitting halfway resumes where it stopped
   * instead of replaying the whole wizard — and re-running every install.
   */
  const go = useCallback(
    (to: Step) => {
      setStep(to);
      void persist({ current_step: to });
    },
    [persist],
  );

  /** Install the resolved modules in order, naming whatever fails. */
  const runInstall = useCallback(async () => {
    setBusy(true);
    setInstalled([]);
    setFailed([]);
    setCatalogError(null);
    const ok: string[] = [];
    const bad: string[] = [];
    try {
      await refreshCatalog.mutateAsync({});
    } catch (err: unknown) {
      // Shown on screen, not swallowed into a log nobody reads: an offline
      // first run must SAY it is offline.
      setCatalogError(err instanceof Error ? err.message : String(err));
    }
    for (const id of plan.modules) {
      try {
        await install.mutateAsync({ key: `module:magnis.${id}` });
        ok.push(id);
        setInstalled([...ok]);
      } catch {
        bad.push(id);
        setFailed([...bad]);
      }
    }
    setBusy(false);
    setStep("done");
    await persist({ current_step: "done", deferred: bad });
  }, [plan.modules, install, refreshCatalog, persist]);

  const finish = useCallback(async () => {
    await persist({ completed: true, current_step: "done", engine });
    onFinished();
  }, [persist, onFinished, engine]);

  // The persisted step is authoritative on mount only; afterwards `go` owns it.
  useEffect(() => {
    if (initialStep === "installing") {
      // An interrupted install resumes as a fresh attempt rather than a
      // half-drawn progress list.
      setStep("modules");
    }
  }, [initialStep]);

  const number = stepNumber(step, hasSources);
  const dots = number === null ? [] : Array.from({ length: number.total }, (_, i) => i);

  // Portalled to <body> on purpose: `position: fixed` is relative to the
  // nearest ancestor with a transform/filter/contain, and inside the app shell
  // that made the overlay cover a column instead of the window — the sidebar
  // and chat panel stayed visible around it. The portal escapes any such
  // containing block, so the backdrop is genuinely full-viewport.
  const shell = (
    title: string,
    subtitle: string,
    body: JSX.Element,
    footer: JSX.Element,
  ): JSX.Element =>
    createPortal(
      <div className="fixed inset-0 z-[1000] flex items-center justify-center overflow-auto bg-black/70 backdrop-blur-sm p-6">
        <div className="w-full max-w-xl rounded-2xl border border-edge bg-surface shadow-2xl p-8 flex flex-col gap-6 my-auto">
          {number !== null && (
            <div className="flex items-center justify-between text-xs text-content-tertiary">
              <span>
                Step {String(number.index)} of {String(number.total)}
              </span>
              <span className="flex gap-1.5">
                {dots.map((i) => (
                  <span
                    key={i}
                    className={`w-1.5 h-1.5 rounded-full ${
                      i < number.index ? "bg-accent" : "bg-edge"
                    }`}
                  />
                ))}
              </span>
            </div>
          )}
          <div>
            <h1 className="text-xl font-semibold text-content">{title}</h1>
            <p className="text-sm text-content-secondary mt-1">{subtitle}</p>
          </div>
          {body}
          <div className="flex justify-end gap-2 pt-1">{footer}</div>
        </div>
      </div>,
      document.body,
    );

  const btn = (label: string, onClick: () => void, primary = false): JSX.Element => (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className={`h-9 px-4 rounded-lg text-sm disabled:opacity-50 ${
        primary
          ? "bg-accent text-white"
          : "border border-edge text-content-secondary hover:text-content"
      }`}
    >
      {label}
    </button>
  );

  if (step === "welcome") {
    return shell(
      "Magnis",
      "Your personal operations system.",
      <div className="text-sm text-content-secondary leading-relaxed">
        Everything lives on this Mac. Your mail, chats, meetings and notes become one connected
        graph you can ask questions about — and nothing leaves your machine unless you connect a
        service yourself.
      </div>,
      btn("Get started", () => { go("modules"); }, true),
    );
  }

  if (step === "modules") {
    return shell(
      "What should Magnis keep for you?",
      "Tap a row to switch it on or off. The ones that need no account are already on.",
      <>
        <Card>
          {CAPABILITIES.map((c: Capability) => (
            <Row
              key={c.id}
              id={c.id}
              title={c.title}
              blurb={
                c.source !== undefined
                  ? `Connects to ${sourceTitle(c.source)} — you sign in next`
                  : undefined
              }
              trailing={<Checkbox checked={picks.includes(c.id)} />}
              onClick={() => { toggle(c.id); }}
            />
          ))}
        </Card>
        <p className="text-xs text-content-tertiary">
          {picks.length === 0
            ? "Nothing selected — Magnis will still open, and you can add these in Settings."
            : `${String(picks.length)} selected · ${String(plan.modules.length)} modules to install`}
        </p>
      </>,
      <>
        {btn("Back", () => { go("welcome"); })}
        {btn("Continue", () => { go(nextStep("modules", hasSources)); }, true)}
      </>,
    );
  }

  if (step === "sources") {
    const raw = sourceList.data;
    const known: readonly SourceInfo[] = Array.isArray(raw)
      ? raw
      : ((raw as { sources?: readonly SourceInfo[] } | undefined)?.sources ?? []);
    return shell(
      "Where should Magnis pull from?",
      "Connect what you want now — the modules install either way.",
      <>
        <Card>
          {plan.sources.map((id) => {
            const info = known.find((k) => k.source_id === id);
            const isConnected = connected.includes(id);
            // Connectable is the backend's own answer, not a guess here.
            const canConnect = info?.connectable === true && info.auth_type != null;
            const reason =
              info?.unavailable_reason ?? "Not available in this build — you can connect it later.";
            return (
              <Row
                key={id}
                id={id}
                title={sourceTitle(id)}
                blurb={isConnected ? undefined : canConnect ? "Tap to sign in" : reason}
                trailing={
                  <Tag
                    label={isConnected ? "connected" : canConnect ? "connect" : "later"}
                    variant={isConnected ? "green" : "default"}
                    mode="subtle"
                  />
                }
                onClick={
                  canConnect && !isConnected
                    ? (): void => { setConnecting({ id, authType: info.auth_type ?? "" }); }
                    : undefined
                }
              />
            );
          })}
        </Card>
        {connecting !== null && (
          <div className="rounded-xl border border-edge bg-surface-tertiary p-4">
            <div className="text-sm font-medium text-content mb-3">
              Connect {sourceTitle(connecting.id)}
            </div>
            {/* The existing auth flow, mounted as-is — OAuth for Google,
                phone+code for Telegram. No second implementation. */}
            <SourceConnect
              sourceId={connecting.id}
              authType={connecting.authType}
              onConnected={(): void => {
                setConnected((c) => [...c, connecting.id]);
                setConnecting(null);
                void sourceList.refetch();
              }}
              onError={(): void => { setConnecting(null); }}
            />
            <button
              type="button"
              onClick={() => { setConnecting(null); }}
              className="mt-3 text-xs text-content-tertiary hover:text-content"
            >
              Cancel
            </button>
          </div>
        )}
      </>,
      <>
        {btn("Back", () => { go(prevStep("sources", hasSources)); })}
        {btn("Continue", () => { go(nextStep("sources", hasSources)); }, true)}
      </>,
    );
  }

  if (step === "agent") {
    // Only what has been PROBED usable is selectable. The static engine list
    // reports claude/builtin/codex on every machine, which is how a Mac with
    // no `claude` came to be offered Claude Code.
    const usable = agents.engines.filter((e) => agents.readiness[e]?.ready === true);
    return shell(
      "Which agent should drive Magnis?",
      usable.length > 0
        ? "Found on this Mac:"
        : "Nothing usable was found on this Mac yet.",
      <>
        {agents.engines.length > 0 && (
          <Card>
            {agents.engines.map((name) => {
              const verdict = agents.readiness[name];
              const ready = verdict?.ready === true;
              return (
                <div key={name} className={ready ? undefined : "opacity-60"}>
                  <AgentRow
                    name={name}
                    active={engine === name}
                    status={
                      ready ? null : (
                        <span className="text-xs text-amber">{readinessLabel(verdict?.reason)}</span>
                      )
                    }
                    onSelect={
                      ready
                        ? (): void => {
                            setEngine(name);
                            // Recording the choice is not the same as making
                            // it the default: `default_engine` lives in
                            // `app_settings` and is what the chat path reads.
                            // Writing only the setup document left a summary
                            // saying "Agent: Claude Code" over a chat still
                            // running on the fallback engine.
                            void agents.setEngine(name);
                            void persist({ engine: name });
                          }
                        : (): void => undefined
                    }
                  />
                  {!ready && verdict?.detail !== undefined && verdict.detail !== "" && (
                    <p className="px-4 pb-3 -mt-1 text-xs text-content-tertiary">
                      {verdict.detail}
                    </p>
                  )}
                </div>
              );
            })}
          </Card>
        )}
        <p className="text-xs text-content-tertiary">
          {usable.length === 0
            ? "Magnis still works — everything except chat. You can set an agent up later in Settings → Agent."
            : "You can change this any time in Settings → Agent."}
        </p>
      </>,
      <>
        {btn("Back", () => { go(prevStep("agent", hasSources)); })}
        {btn(
          engine === null ? "Skip for now" : "Continue",
          () => { go(nextStep("agent", hasSources)); },
          true,
        )}
      </>,
    );
  }

  if (step === "search") {
    return shell(
      "How should Magnis search your data?",
      "Bigger models understand meaning, but download first. You can change this any time.",
      // The Settings panel, mounted as-is: it already lists every model with
      // its size and cached state and polls `search.model_status` to watch a
      // download land. Restating it here would be a second place to keep in
      // sync — and a second place to get wrong.
      <SearchIndexingPanel />,
      <>
        {btn("Back", () => { go(prevStep("search", hasSources)); })}
        {btn("Continue", () => { setStep("installing"); void runInstall(); }, true)}
      </>,
    );
  }

  if (step === "installing") {
    const doneCount = installed.length + failed.length;
    return shell(
      "Setting up Magnis…",
      `Installing the modules you picked — ${String(doneCount)} of ${String(plan.modules.length)}.`,
      <>
        {catalogError !== null && (
          <div className="rounded-lg border border-amber/30 bg-amber/10 p-3 text-xs text-amber">
            Couldn&apos;t reach the module catalog: {catalogError}
          </div>
        )}
        <Card>
          {plan.modules.map((m) => (
            <Row
              key={m}
              id={m}
              title={m}
              trailing={
                <Tag
                  label={
                    installed.includes(m)
                      ? "installed"
                      : failed.includes(m)
                        ? "failed"
                        : "waiting"
                  }
                  variant={installed.includes(m) ? "green" : failed.includes(m) ? "red" : "default"}
                  mode="subtle"
                />
              }
            />
          ))}
        </Card>
      </>,
      <span className="text-xs text-content-tertiary">This takes a moment…</span>,
    );
  }

  const engineLine =
    engine === null
      ? "No agent yet — Magnis works, chat needs an agent (Settings → Agent)."
      : `Agent: ${engineMeta(engine).label}.`;
  // Counted from the RECORD, not from this render's progress list: resuming
  // onto this screen after a restart would otherwise report "0 modules
  // installed" for a run that installed everything.
  const succeeded = plan.modules.length - failed.length;
  return shell(
    "You're set up",
    failed.length > 0
      ? `${String(succeeded)} of ${String(plan.modules.length)} modules installed.`
      : `${String(succeeded)} modules installed.`,
    <div className="text-sm text-content-secondary space-y-2">
      {/* Only worth saying when it actually cost something. Announcing an
          unreachable catalog next to "8 modules installed" reads as a
          contradiction. */}
      {catalogError !== null && failed.length > 0 && (
        <p className="text-amber">
          The module catalog was unreachable, so some modules were skipped. They stay available in
          Settings → Extensions.
        </p>
      )}
      {failed.length > 0 && (
        <p>
          Couldn&apos;t install: {failed.join(", ")}. They stay available in Settings → Extensions.
        </p>
      )}
      {plan.sources.length > 0 && (
        <p>
          Connect {plan.sources.map(sourceTitle).join(", ")} whenever you like in Settings →
          Integrations.
        </p>
      )}
      <p>{engineLine}</p>
    </div>,
    <>
      {failed.length > 0 &&
        btn("Try again", () => { setStep("installing"); void runInstall(); })}
      {btn("Open Magnis", () => { void finish(); }, true)}
    </>,
  );
}
